import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ChevronRight, type LucideIcon } from 'lucide-react';

/**
 * Lista de renglones de Foco: sin recuadro, con separadores discretos entre
 * renglones (design.md, 12.5).
 *
 * Con `tarjetas` cada renglón va en su propio panel, separados por aire en
 * lugar de líneas (reacomodo de la 0.11.0): así se ven las rutinas numeradas
 * de Gym. Con `rejilla`, esos paneles se acomodan en dos columnas, como los
 * accesos de Comida y las herramientas de Gym.
 */
export function Filas({
  children,
  className = '',
  tarjetas = false,
  rejilla = false,
}: {
  children: ReactNode;
  className?: string;
  tarjetas?: boolean;
  rejilla?: boolean;
}) {
  const base = rejilla
    ? 'grid grid-cols-2 gap-2'
    : tarjetas
      ? 'space-y-2'
      : 'divide-y divide-borde';
  return <ul className={`${base} ${className}`.trim()}>{children}</ul>;
}

interface PropsFila {
  titulo: ReactNode;
  detalle?: ReactNode;
  icono?: LucideIcon;
  /** Si se indica, el renglón entero es un enlace con flecha. */
  a?: string;
  /** Algo a la derecha antes de la flecha, como una pastilla de fase. */
  extra?: ReactNode;
  /** Columna izquierda de texto, por ejemplo "01", en lugar de ícono. */
  marca?: string;
  /** El renglón va en su propio panel, dentro de unas `Filas` con `tarjetas`. */
  tarjeta?: boolean;
}

/** Renglón con ícono de trazo fino, título, detalle y flecha si lleva a otra pantalla. */
export function Fila({ titulo, detalle, icono: Icono, a, extra, marca, tarjeta = false }: PropsFila) {
  const contenido = (
    <>
      {Icono !== undefined && (
        <span className="grid w-8 shrink-0 place-items-center text-marca">
          <Icono className="size-5" />
        </span>
      )}
      {marca !== undefined && (
        <span className="w-[29px] shrink-0 text-[16px] tabular-nums text-marca">{marca}</span>
      )}
      <span className="grid min-w-0 flex-1 gap-[3px]">
        <span className="text-[14px] font-medium text-texto">{titulo}</span>
        {detalle !== undefined && <span className="text-[12px] text-texto-suave">{detalle}</span>}
      </span>
      {extra}
      {a !== undefined && <ChevronRight className="size-4 text-texto-suave" />}
    </>
  );

  const clases = `flex w-full items-center gap-[13px] text-left ${
    tarjeta ? 'h-full rounded-panel bg-superficie px-4 py-[13px]' : 'py-4'
  }`;
  return (
    <li>
      {a !== undefined ? (
        <Link to={a} className={`${clases} min-h-14 active:opacity-60`}>
          {contenido}
        </Link>
      ) : (
        <div className={clases}>{contenido}</div>
      )}
    </li>
  );
}
